import { Controller } from 'stimulus'
import { Calendar } from '@fullcalendar/core'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { DateTime as dateTime } from 'luxon'
import luxonPlugin, { toLuxonDateTime } from '@fullcalendar/luxon2'
import classNames from 'classnames'
import './calendar_component.scss'
import { CalendarEventStore } from '~/common/components/calendar/event_store'
import allLocales from '@fullcalendar/core/locales-all'
import { getCsrfParamsAsJSON } from '~/common/helpers/csrf'
import {
  fullCalendarTimeFormat,
  getIs24h,
} from '~/controllers/booking/time_format'

export default class extends Controller {
  static targets = [
    'calendar',
    'overlayToggle',
    'overlayToggleInput',
    'timeZoneSelect',
    'timeZoneSelectContainer',
    'subheader',
    'freeSlotToggleInput',
    'events',
    'openEventIconTemplate',
  ]
  static values = {
    feedUrl: String,
    locale: String,
    view: String,
    store: String,
    allowViewSwitch: Boolean,
    startDate: String,
    firstDayOfWeek: Number,
    timeZone: String,
    selectable: Boolean,
    slotDuration: { type: Number, default: 15 },
    persistTimeFormat: { type: Boolean, default: false },
    schedulingSelection: { type: Boolean, default: false },
    keyboardNavigation: { type: Boolean, default: false },
  }

  connect() {
    this.eventStore = new CalendarEventStore({
      feedUrl: this.feedUrlValue,
      overlayToggleInput: this.hasOverlayToggleInputTarget
        ? this.overlayToggleInputTarget
        : undefined,
    })
    this.calendar = new Calendar(this.calendarTarget, {
      timeZone: this.hasTimeZoneSelectTarget
        ? this.timeZoneSelectTarget.value || undefined
        : this.timeZoneValue || undefined,
      locales: allLocales,
      locale: this.localeValue,
      plugins: [luxonPlugin, timeGridPlugin, dayGridPlugin, interactionPlugin],
      initialView: this.viewValue,
      firstDay: this.firstDayOfWeekValue,
      height: '100%',
      headerToolbar: {
        left: this.allowViewSwitchValue ? 'dayGridMonth,timeGridWeek' : 'title',
        center: this.allowViewSwitchValue ? 'title' : undefined,
        right: 'today prev,next',
      },
      buttonText: {
        timeGridWeek: 'Week',
        dayGridMonth: 'Month',
      },
      titleFormat: '{LLL} yyyy',
      slotDuration:
        { 15: '00:15:00', 30: '00:30:00', 60: '01:00:00' }[
          this.slotDurationValue
        ] ?? '00:15:00',
      slotLabelInterval: '01:00:00',
      ...(this.persistTimeFormatValue
        ? {
            slotLabelFormat: fullCalendarTimeFormat(),
            eventTimeFormat: fullCalendarTimeFormat(),
          }
        : {}),
      scrollTimeReset: false,
      expandRows: true,
      lazyFetching: this.storeValue == 'url',
      dayMaxEvents: 6,
      eventMaxStack: 4,
      initialDate: this.startDateValue || new Date(),
      dayHeaderContent: this.renderDayHeaderContent,
      eventContent: this.renderEventContent,
      allDaySlot: false,
      // FullCalendar select waits for pointerup and used to be enabled by the
      // admin scheduling URL. The scheduling-link show paints its draft on
      // pointerdown instead. Leave this off for every calendar.
      selectable: false,
      events:
        this.storeValue == 'url'
          ? this.eventStore.fetchEvents
          : (info, successCallback) =>
              this.provideJsonEvents(info, successCallback),
      viewClassNames: () => {
        this.alterButtons()
      },

      datesSet: () => {
        this.updateTitleRange()
        this.placeHint()
      },

      windowResize: () => this.placeHint(),

      viewDidMount: () => {
        const elm = this.calendar.el.querySelector('.fc-timegrid-axis-frame')
        if (elm) {
          const timeZone = dateTime
            .now()
            .setZone(this.calendar.view.dateEnv.timeZone)
            .toFormat('ZZZZ')
          elm.textContent = timeZone
        }
      },

      eventClassNames: (arg) => {
        const event = arg.event
        if (event.start && event.end) {
          const minutes = (event.end - event.start) / (1000 * 60)
          return minutes <= 20
            ? 'calendar-event-very-short'
            : minutes <= 30
            ? 'calendar-event-short'
            : null
        }
      },

      // fullcalendar is bad in that it does not invoke handlers attached through `this.calendar.on` (some of them)
      // so we listen for those events and trigger a custom event on the dom element
      eventClick: (arg) => {
        // Handle override event click - open edit modal
        if (arg.event.id && arg.event.id.startsWith('override-')) {
          arg.jsEvent.preventDefault()
          this.calendar.el.dispatchEvent(
            new CustomEvent('simplero:overrideEdit', {
              detail: arg,
              bubbles: true,
            })
          )
          return
        }
        if (arg.event.url) {
          arg.jsEvent.preventDefault()
          // Inside the mobile app's WebView, a new-window request from this non-anchor
          // FullCalendar chip can't be hit-tested by the native handler and silently does
          // nothing (ENG-34417), so navigate the WebView itself there instead.
          if (document.documentElement.classList.contains('turbo-native')) {
            window.location.href = arg.event.url
          } else {
            window.open(arg.event.url, '_blank')
          }
        }
        this.calendar.el.dispatchEvent(
          new CustomEvent('simplero:eventClick', { detail: arg, bubbles: true })
        )
      },
      eventDidMount: (arg) =>
        this.calendar.el.dispatchEvent(
          new CustomEvent('simplero:eventDidMount', { detail: arg })
        ),
    })

    if (this.hasTimeZoneSelectContainerTarget) {
      this.initializeTimeZoneSelect()
    } else if (this.timeZoneValue) {
      this.calendar.setOption('timeZone', this.timeZoneValue)
    }

    if (this.storeValue == 'url') {
      this.eventStore.bindCalendar(this.calendar)
    }

    this.calendar.render()

    if (import.meta.env.DEV) {
      setInterval(() => this.calendar.render(), 1000)
    }

    document
      .querySelectorAll('[data-behavior*=refresh-calendar-on-change')
      .forEach((el) => {
        el.addEventListener('change', this.refresh.bind(this))
      })

    // Listen for refresh-calendar event from availability override controller
    this.element.addEventListener('refresh-calendar', this.refresh.bind(this))

    // Live-update time formatting when the visitor toggles 24h (booking page only).
    if (this.keyboardNavigationValue)
      document.addEventListener('keydown', this.onKeydown)
    if (this.persistTimeFormatValue) {
      document.addEventListener('time-format:change', this.onTimeFormatChange)
    }
  }

  disconnect() {
    this.hintObserver?.disconnect()
    if (this.keyboardNavigationValue)
      document.removeEventListener('keydown', this.onKeydown)
    if (this.persistTimeFormatValue) {
      document.removeEventListener(
        'time-format:change',
        this.onTimeFormatChange
      )
    }
  }

  onTimeFormatChange = () => {
    if (!this.calendar) return
    const fmt = fullCalendarTimeFormat()
    this.calendar.setOption('slotLabelFormat', fmt)
    this.calendar.setOption('eventTimeFormat', fmt)
    // Fresh function identity => FullCalendar re-runs our custom content for
    // already-displayed events (e.g. an overlayed calendar) without a refetch.
    this.calendar.setOption('eventContent', (arg) =>
      this.renderEventContent(arg)
    )
  }

  refresh = () => {
    this.eventStore.clearStore()
    this.calendar.refetchEvents()
  }

  // `url` store fetches a feed. `json` store is for CustomView calendars that
  // listen for `calendar-view:change` and reply with `update-calendar-events`.
  // `static` store (builder Events listing) already has the full occurrence set
  // on the page — return it on every range request so prev/next month still works.
  provideJsonEvents(info, successCallback) {
    if (this.storeValue == 'static' || !this.calendar?.view) {
      successCallback(this.parseEventsPayload())
      return
    }

    setTimeout(() => {
      const event = new CustomEvent('calendar-view:change', {
        bubbles: true,
        detail: {
          start: this.calendar?.view.currentStart?.toISOString()?.split('T')[0],
          end: this.calendar?.view.currentEnd?.toISOString()?.split('T')[0],
          calendar_date: this.calendar?.view.currentStart?.toISOString(),
          timeZone: info.timeZone,
          calendarView: this.calendar?.view?.type,
        },
      })

      const handler = (e) => {
        successCallback(e.detail.events)

        this.element.removeEventListener('update-calendar-events', handler)
      }

      this.element.addEventListener('update-calendar-events', handler)

      this.calendarTarget.dispatchEvent(event)
    }, 100)
  }

  parseEventsPayload() {
    try {
      return JSON.parse(this.eventsTarget.innerText)
    } catch (e) {
      console.error(e)
      return []
    }
  }

  // Arrow keys and j/k page through weeks, unless the visitor is typing, a media player or another
  // handler took the key, this calendar is not on screen (the phone layout hides it), or a popup,
  // dropdown or modal has the keyboard. A focused button is fine: clicking Today or Next leaves it
  // focused in Chrome and Firefox, and the arrow keys must keep working right after that.
  onKeydown = (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return
    if (!this.element.offsetParent || this.element.closest('[inert]')) return
    if (
      e.target.closest?.(
        'input, textarea, select, [contenteditable="true"], video, audio, .video-js'
      )
    )
      return
    if (
      document.querySelector(
        '.tippy-box[data-state="visible"], [data-controller~="modal"].in'
      )
    )
      return
    if (e.key === 'ArrowRight' || e.key === 'j') this.calendar.next()
    else if (e.key === 'ArrowLeft' || e.key === 'k') this.calendar.prev()
    else return
    e.preventDefault()
  }

  initializeTimeZoneSelect = () => {
    if (this.hasTimeZoneSelectContainerTarget) {
      this.timeZoneSelectContainerTarget.classList.remove('opacity-0')
    }
  }

  changeTimeZone = (e) => {
    this.calendar.setOption('timeZone', this.timeZoneSelectTarget.value)
    this.dispatch('timeZoneChanged', {
      detail: { timeZone: this.timeZoneSelectTarget.value },
    })
    const elm = this.calendar.el.querySelector('.fc-timegrid-axis-frame')
    if (elm) {
      const timeZone = dateTime
        .now()
        .setZone(this.calendar.view.dateEnv.timeZone)
        .toFormat('ZZZZ')
      elm.textContent = timeZone
    }

    this.alteredButtons = false
    this.alterButtons()
  }

  alterButtons = () => {
    // fullcalendar's API to tweak classnames on buttons is non-existent. So we need to attach into the viewDidMount render hook and manually alter classes.
    this.alteredButtons = true
    this.calendarTarget.querySelectorAll('.fc-button').forEach((button) => {
      const classReplacementMap = {
        'fc-button': ['t-btn', 't-btn-sm'],
        'fc-button-primary': ['t-btn-secondary'],
      }
      Object.entries(classReplacementMap).forEach(([orig, replacement]) => {
        if (button.classList.contains(orig)) {
          button.classList.remove(orig)
          button.classList.add(...replacement)
        }
      })
      if (
        button.children.length == 1 &&
        button.children[0].classList.contains('fc-icon')
      ) {
        button.classList.add('t-btn-icon-only')
      }
      if (button.classList.contains('fc-prev-button')) {
        button.parentElement.classList.add('calendar-prev-next-buttons')
        const container = button.parentElement.parentElement
        if (this.hasOverlayToggleTarget) {
          this.overlayToggleTarget.classList.remove('hidden')
          container.prepend(this.overlayToggleTarget)
        }
        container.classList.add('calendar-nav-buttons')
        this.placeSubheader()
      }
    })
  }

  // The subheader slot renders outside FullCalendar's DOM; move it under the toolbar once it exists.
  placeSubheader = () => {
    if (!this.hasSubheaderTarget) return
    const toolbar = this.calendarTarget.querySelector('.fc-header-toolbar')
    if (!toolbar || this.subheaderTarget.previousElementSibling === toolbar)
      return
    this.subheaderTarget.classList.remove('hidden')
    toolbar.after(this.subheaderTarget)
    // The subheader grows when the device note appears: the floating hint must follow the grid down.
    this.hintObserver ??= new ResizeObserver(() => this.placeHint())
    this.hintObserver.observe(this.subheaderTarget)
    this.placeHint()
  }

  // The booking page's "Select an open slot" hint floats over the grid: keep it just below the day
  // headers, whatever sits above them (toolbar height, the device note row).
  placeHint = () => {
    const hint = this.element.querySelector('.js-calendar-hint')
    // The day header row, not the grid body: the body scrolls (to the first slot) and can sit above the fold.
    const header = this.calendarTarget.querySelector('.fc-col-header')
    if (!hint || !header || !hint.offsetParent) return
    const top =
      header.getBoundingClientRect().bottom -
      hint.offsetParent.getBoundingClientRect().top
    hint.style.top = `${Math.round(top + 24)}px`
  }

  // Admin scheduling link: "Oct 2026" alone doesn't say which week is on screen.
  updateTitleRange = () => {
    if (!this.schedulingSelectionValue) return
    const title = this.calendarTarget.querySelector('.fc-toolbar-title')
    if (!title) return
    let range = title.querySelector('.js-calendar-title-range')
    const view = this.calendar.view
    if (view.type !== 'timeGridWeek') {
      range?.remove()
      return
    }
    if (!range) {
      range = document.createElement('span')
      range.className =
        'js-calendar-title-range ml-2 text-sm font-normal text-fg-muted'
      title.append(range)
    }
    range.textContent = this.calendar.formatRange(
      view.currentStart,
      new Date(view.currentEnd.getTime() - 1),
      { month: 'short', day: 'numeric' }
    )
  }

  renderDayHeaderContent = (arg) => {
    /**
     * Ok, let me explain this mess.
     * `arg.date` comes in a JS object that is ALREADY modified according to the locale set in calendar timeZone property.
     * So we need to undo that by calculating the offset of the locale and adding it back to the date.
     *
     * And then also UTC it to offset the browser offsetting the JS date.
     */
    const localeDate = toLuxonDateTime(new Date(), this.calendar)
    const currentOffset = new Date().getTimezoneOffset()
    const argOffset = arg.date.getTimezoneOffset()
    const zone = this.calendar.getOption('timeZone')
    localeDate.setZone(zone)
    let date = toLuxonDateTime(arg.date, this.calendar).toUTC()

    /**
     * Of course, it wouldnt be that easy, right?
     * Apparently, intentionally, fullcalendar sends arg.date in  distant past 1970 when in monthly view
     * https://github.com/fullcalendar/fullcalendar/issues/5854
     *
     * so when that happens, just UTC the thing and it seems to correcty put the date back to 0 hour.
     */
    if (this.calendar.view.type === 'timeGridWeek') {
      date = date.plus({
        minutes: zone
          ? localeDate.offset
          : argOffset != currentOffset
          ? 0
          : currentOffset * -1,
      })
    }

    // There is an issue with a misinterpretation of offsets to DST, causing some dates to be off by (usually) 1 hour.
    // causing the dates the have hours set to 23 of the previous
    if (date.hour >= 21) {
      date = date.plus({
        hours: 24 - date.hour,
      })
    }

    // Admin recolors .calendar-weekday-label in calendar_component.scss; other calendars keep text-neutral-500
    const html = `
      <span class="flex items-center gap-x-2 font-normal">
        <span class="calendar-weekday-label text-neutral-500 text-sm">${date.toFormat(
          'ccc'
        )}</span>
        <span class="${classNames(
          `text-lg font-semibold rounded-full h-9 w-9  ${
            arg.view.type === 'dayGridMonth'
              ? 'hidden'
              : 'quick-inline-flex-center'
          }`,
          arg.isToday && 'bg-primary-500 text-neutral-0'
        )}">${date.toFormat('d')}</span>
      </span>
    `
    return { html }
  }

  renderEventContent = (arg) => {
    const groupId = arg.event.groupId
    if (groupId === 'available-hover' || groupId === 'available-active') {
      // Use the pre-formatted title which respects the calendar's selected timezone
      // (set in desktop_booking_flow.js using Luxon with getTimeZoneFromFullcalendar)
      if (arg.event.title) {
        return {
          html: `<div class="fc-event-time">${arg.event.title}</div>`,
        }
      }
      return undefined
    }

    const props = arg.event.extendedProps || {}
    if (
      !props.image_url &&
      !props.subtitle &&
      !props.host_avatars?.length &&
      !props.occurrence_title
    ) {
      const selectedZone = this.calendar.getOption('timeZone')
      const fmt = (d) =>
        d.toLocaleTimeString([], {
          timeZone: selectedZone === 'local' ? undefined : selectedZone,
          hour: 'numeric',
          minute: '2-digit',
          ...(this.persistTimeFormatValue ? { hour12: !getIs24h() } : {}),
        })
      const timeStr =
        arg.event.start && arg.event.end
          ? `<span class="calendar-event-start-time">${fmt(
              arg.event.start
            )}</span><span class="calendar-event-end-time"> - ${fmt(
              arg.event.end
            )}</span>`
          : ''
      // A linked overlay event lets drags pass through, so this icon is what still opens it:
      // its click bubbles up to eventClick (ENG-35659).
      const openIcon =
        props.show_open_icon && this.hasOpenEventIconTemplateTarget
          ? this.openEventIconTemplateTarget.innerHTML
          : ''
      if (arg.event.title) {
        const title = `<div class="fc-event-title">${this.escapeHtml(
          arg.event.title
        )}</div>`
        const time = timeStr ? `<div class="fc-event-time">${timeStr}</div>` : ''
        return {
          html: `<div class="fc-event-content">${openIcon}${
            this.element.closest('#admin-events_index')
              ? time + title
              : title + time
          }</div>`,
        }
      }
      return undefined
    }

    const isDayGrid = arg.view.type === 'dayGridMonth'
    if (!isDayGrid) return undefined // only render cards in month view

    const parts = []

    // Image + title row
    const safeImgUrl = this.sanitizeUrl(props.image_url)
    const imgHtml = safeImgUrl
      ? `<img src="${safeImgUrl}" class="calendar-event-card-img" />`
      : ''
    parts.push(
      `<div class="calendar-event-card-header">${imgHtml}<div class="calendar-event-card-titles"><div class="calendar-event-card-title">${this.escapeHtml(
        arg.event.title
      )}</div>`
    )
    if (props.subtitle)
      parts.push(
        `<div class="calendar-event-card-subtitle">${this.escapeHtml(
          props.subtitle
        )}</div>`
      )
    parts.push('</div></div>')

    // Host avatars
    if (props.host_avatars?.length) {
      const avatars = props.host_avatars
        .map((h) => {
          const safeUrl = this.sanitizeUrl(h.url)
          return safeUrl
            ? `<img src="${safeUrl}" title="${this.escapeHtml(
                h.name
              )}" class="calendar-event-card-avatar" />`
            : `<span title="${this.escapeHtml(
                h.name
              )}" class="calendar-event-card-avatar calendar-event-card-avatar-initials">${this.escapeHtml(
                (h.name || '?')[0]
              )}</span>`
        })
        .join('')
      parts.push(`<div class="calendar-event-card-avatars">${avatars}</div>`)
    }

    // Occurrence title (if different from event title)
    if (props.occurrence_title)
      parts.push(
        `<div class="calendar-event-card-occurrence">${this.escapeHtml(
          props.occurrence_title
        )}</div>`
      )

    return { html: `<div class="calendar-event-card">${parts.join('')}</div>` }
  }

  sanitizeUrl(url) {
    if (!url) return ''
    try {
      const parsed = new URL(url, window.location.origin)
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : ''
    } catch {
      return ''
    }
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getBrowserLocale() {
    const locale = navigator.languages
      ? navigator.languages[0]
      : navigator.language || navigator.userLanguage

    return locale.toLowerCase()
  }

  onFreeSlotSwitchChanged() {
    fetch('/calendars/toggle_free_slot_display', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: getCsrfParamsAsJSON(),
    }).then(this.refresh)
  }

  jumpDate({ detail }) {
    if (detail.date) {
      this.calendar.gotoDate(detail.date)
    }
  }
}
